import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Search, RefreshCw, MessageSquare, Users, Calendar, Eye } from 'lucide-react';
import { ThreadDetailModal } from './ThreadDetailModal';

const GET_MESSAGE_THREADS = gql`
  query GetMessageThreads($limit: Int, $offset: Int, $search: String) {
    getMessageThreads(limit: $limit, offset: $offset, search: $search) {
      threads {
        uniqueid
        subject
        toJson
        ccJson
        replyJson
        tags
        createdAt
        messageCount
        lastMessageAt
      }
      totalCount
    }
  }
`;

export const MessageThreadsView: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery(GET_MESSAGE_THREADS, {
    variables: {
      limit: pageSize,
      offset: page * pageSize,
      search: searchTerm || undefined
    },
    fetchPolicy: 'cache-first'
  });

  const handleRefresh = () => {
    refetch();
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(0); // Reset to first page when searching
  };

  const parseJsonSafely = (jsonStr: string | null): string[] => {
    if (!jsonStr) return [];
    try {
      const parsed = JSON.parse(jsonStr);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 flex-1 max-w-md" />
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-24" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load message threads: {error.message}
        </AlertDescription>
      </Alert>
    );
  }

  const { threads = [], totalCount = 0 } = data?.getMessageThreads || {};
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search threads by subject..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={pageSize.toString()} onValueChange={(value) => setPageSize(parseInt(value))}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="25">25 per page</SelectItem>
            <SelectItem value="50">50 per page</SelectItem>
            <SelectItem value="100">100 per page</SelectItem>
          </SelectContent>
        </Select>

        <Button onClick={handleRefresh} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="text-sm text-gray-600">
        Found {totalCount} message threads
      </div>

      {/* Threads Table */}
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Subject</TableHead>
              <TableHead>Participants</TableHead>
              <TableHead>Messages</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Last Message</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {threads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  {searchTerm ? `No threads found matching "${searchTerm}"` : 'No message threads found'}
                </TableCell>
              </TableRow>
            ) : (
              threads.map((thread: any) => {
                const toEmails = parseJsonSafely(thread.toJson);
                const ccEmails = parseJsonSafely(thread.ccJson);
                const allParticipants = [...toEmails, ...ccEmails].filter(Boolean);
                
                return (
                  <TableRow key={thread.uniqueid}>
                    <TableCell>
                      <div className="font-medium">{thread.subject || 'No Subject'}</div>
                      <div className="text-xs text-gray-500">ID: {thread.uniqueid}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{allParticipants.length}</span>
                      </div>
                      {allParticipants.length > 0 && (
                        <div className="text-xs text-gray-500 mt-1 max-w-xs truncate">
                          {allParticipants.slice(0, 2).join(', ')}
                          {allParticipants.length > 2 && ` +${allParticipants.length - 2} more`}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{thread.messageCount}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {thread.tags ? (
                        <Badge variant="secondary" className="text-xs">
                          {thread.tags}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(thread.createdAt).toLocaleDateString()}
                      </div>
                      <div className="text-xs">
                        {new Date(thread.createdAt).toLocaleTimeString()}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {thread.lastMessageAt && (
                        <>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(thread.lastMessageAt).toLocaleDateString()}
                          </div>
                          <div className="text-xs">
                            {new Date(thread.lastMessageAt).toLocaleTimeString()}
                          </div>
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedThreadId(thread.uniqueid)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, totalCount)} of {totalCount} threads
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(0)}
            disabled={page === 0}
          >
            First
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page - 1)}
            disabled={page === 0}
          >
            Previous
          </Button>
          <span className="text-sm">
            Page {page + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages - 1}
          >
            Next
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(totalPages - 1)}
            disabled={page >= totalPages - 1}
          >
            Last
          </Button>
        </div>
      </div>

      {/* Thread Detail Modal */}
      {selectedThreadId && (
        <ThreadDetailModal
          threadId={selectedThreadId}
          isOpen={Boolean(selectedThreadId)}
          onClose={() => setSelectedThreadId(null)}
        />
      )}
    </div>
  );
};
